import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { fadeIn } from '@sorare/core/src/atoms/animations/keyframes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Gift } from '@sorare/core/src/atoms/icons/Gift';
import { PlayerLoyalty } from '@sorare/core/src/atoms/icons/PlayerLoyalty';
import { VaultBoost } from '@sorare/core/src/atoms/icons/VaultBoost';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineM, LabelL } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_MY_CARDS_CARDS_TYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';

import { RarityVaultHowItWorksDialogWithFetch } from '#src/components/sealing/RarityVaultHowItWorksDialog/index.withFetch.tsx';
import { SealingGenericAvatar } from '#src/components/sealing/SealingGenericAvatar/index.tsx';

const SealedCardSkeleton = styled(Vertical).attrs({ gap: 3, center: true })`
  width: var(--sealed-vault-width);
  height: var(--sealed-vault-height);
  padding: calc(5 * var(--unit));
  display: flex;
  text-align: center;
`;

const StyledSealingGenericAvatar = styled(SealingGenericAvatar)`
  margin-top: var(--quadruple-unit);
`;

const ButtonGroup = styled(Horizontal).attrs({ gap: 2, center: true })`
  flex-wrap: wrap;
  width: 100%;

  opacity: 0;
  animation: ${fadeIn} 0.1s ease-in forwards 1.5s;

  & > * {
    flex: 0 1 40%;
  }
`;

type Props = {
  rarity?: Rarity;
};
export const EmptyVaultPlaceholderMissionsBody = ({
  rarity = Rarity.limited,
}: Props) => {
  const [showHowItWorksDialog, setShowHowItWorksDialog] = useState(false);
  const { generateSportContextPath } = useSportContext();

  const cardsTypePath = generateSportContextPath(
    ANY_SPORT_MY_CARDS_CARDS_TYPE,
    {
      params: {
        type: rarity,
      },
    }
  );
  return (
    <>
      <RarityVaultHowItWorksDialogWithFetch
        open={showHowItWorksDialog}
        rarity={rarity}
        onClose={() => setShowHowItWorksDialog(false)}
      />
      <SealedCardSkeleton>
        <StyledSealingGenericAvatar />
        <Horizontal gap={4}>
          <VaultBoost size={3} />
          <PlayerLoyalty size={4} />
          <Gift size={2.5} />
        </Horizontal>
        <Vertical gap={4}>
          <Vertical gap={0.5}>
            <HeadlineM>
              <FormattedMessage
                id="Collections.sealing.skeleton.title"
                defaultMessage="You don't have any sealed cards"
              />
            </HeadlineM>
            <LabelL color="var(--c-nd-600)">
              <FormattedMessage
                id="Collections.sealing.skeleton.subtitle"
                defaultMessage="Sealing cards increases your Vault and Player Loyalty levels, unlocking perks like boosted gameplay rewards."
              />
            </LabelL>
          </Vertical>
          <ButtonGroup gap={1}>
            <Button
              color="tertiary"
              size="small"
              onClick={() => setShowHowItWorksDialog(true)}
            >
              <FormattedMessage {...glossary.learnMore} />
            </Button>
            <Button color="primary" size="small" to={cardsTypePath}>
              <FormattedMessage {...fantasy.startSealing} />
            </Button>
          </ButtonGroup>
        </Vertical>
      </SealedCardSkeleton>
    </>
  );
};
